import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { IconButton, MenuItem, Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import { FormikSelect } from '../../../common/formik-material-ui';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import InfoPanel from '../InfoPanel/InfoPanel';

const useStyles = makeStyles((theme: Theme) => ({
  closeIcon: {
    position: 'absolute',
    top: '10px',
    right: '20px',
  },
  icon: {
    height: 'max-content',
    alignSelf: 'center',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    width: '20vw',
  },
  fieldInsideRow: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '190px',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
    height: 'max-content',
    alignSelf: 'center',
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
}));
// Cadence - Dispositions Tab
export function Dispositions({ filteredDispoList, dispoList, formik }) {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const powerlistReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getBusinessPowerlists',
      }),
    })
  );

  const powerlists = useMemo(
    () =>
      (powerlistReportLoadable?.contents?.response as AxiosResponse)?.data
        ?.powerlists ?? powerlists,
    [powerlistReportLoadable]
  );

  const filterOptions = (index: number) => {
    const currDispoId =
      formik.values.cadenceDispositionRules?.[index].businessDispositionId;
    let firstIndex: number | undefined;
    for (let i = 0; i < formik.values.cadenceDispositionRules.length; i++) {
      const rule = formik.values.cadenceDispositionRules[i];

      if (
        Number(rule.businessDispositionId) === Number(currDispoId) &&
        rule.action === 'stop'
      ) {
        if (!firstIndex) {
          firstIndex = i;
        }
      }
    }
    const firstStop = index === firstIndex;

    if (firstIndex === undefined || firstStop) {
      return [
        { value: 'forward', text: 'Forward To Cadence' },
        { value: 'forwardToPowerlist', text: 'Forward To PowerList' },
        { value: 'stop', text: 'Stop Cadence' },
        { value: 'assignOwner', text: 'Assign Owner' },
      ];
    } else {
      return [
        { value: 'forward', text: 'Forward To Cadence' },
        { value: 'forwardToPowerlist', text: 'Forward To PowerList' },
        { value: 'assignOwner', text: 'Assign Owner' },
      ];
    }
  };

  const filterPowerlists = (index: number) => {
    const correspondingFPLid =
      formik.values.cadenceDispositionRules?.[index]?.forwardToPowerlistId;
    const dispoId =
      formik.values.cadenceDispositionRules?.[index]?.businessDispositionId;
    const arrOfSelectedPowerlistIds: any[] = [];
    for (let i = 0; i < formik.values.cadenceDispositionRules?.length; i++) {
      const r = formik.values.cadenceDispositionRules[i];
      if (
        r.action !== 'stop' &&
        r.forwardToPowerlistId &&
        r.businessDispositionId == dispoId
      ) {
        arrOfSelectedPowerlistIds.push(r.forwardToPowerlistId);
      }
    }
    return powerlists
      ? powerlists
          ?.filter((powerlist) => powerlist.isAgentPowerlist !== 1)
          ?.filter(
            (powerlist) =>
              powerlist.id === correspondingFPLid ||
              !arrOfSelectedPowerlistIds.includes(powerlist.id)
          )
          ?.sort((a, b) =>
            a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
          )
      : [];
  };

  const filterCadences = (index: number) => {
    const correspondingCDid =
      formik.values.cadenceDispositionRules?.[index]?.forwardToCadenceId;
    const dispoId =
      formik.values.cadenceDispositionRules?.[index]?.businessDispositionId;
    const arrOfSelectedCadenceIds: any[] = [];
    for (let i = 0; i < formik.values.cadenceDispositionRules?.length; i++) {
      const r = formik.values.cadenceDispositionRules[i];
      if (
        r.action !== 'stop' &&
        r.forwardToCadenceId &&
        r.businessDispositionId == dispoId
      ) {
        arrOfSelectedCadenceIds.push(r.forwardToCadenceId);
      }
    }

    return cadences
      ? cadences
          ?.filter((cadence) => cadence.cadenceId !== +id)
          ?.filter(
            (cadence) =>
              cadence.cadenceId === correspondingCDid ||
              !arrOfSelectedCadenceIds.includes(cadence.cadenceId)
          )
          ?.sort((a, b) =>
            a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
          )
      : [];
  };
  const cadenceReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_CADENCE_REPORT}?filter=businessId:${userAtomContents?.businessid}`,
    })
  );
  const cadences = useMemo(
    () =>
      cadenceReportLoadable.state === 'hasValue'
        ? [
            ...(cadenceReportLoadable.contents?.response?.data ?? []),
          ]?.sort((a, b) =>
            a?.cadenceName
              ?.toLowerCase()
              ?.localeCompare(b?.cadenceName?.toLowerCase())
          ) ?? []
        : [],
    [cadenceReportLoadable]
  );
  return (
    <div className="container">
      <InfoPanel>
        <div>
          Set your Cadence Call Outcome Rules on this tab. Select from existing
          Call Outcomes on your account, then select an Action. When the
          selected Call Outcome is selected for Cadence calls, the selected
          Action will take effect. Click the + icon to add another Call Outcome
          Rule, click the trash icon to remove a rule.
        </div>
      </InfoPanel>
      {formik.values.cadenceDispositionRules.map((dispo, index) => (
        <div key={index} className="row" style={{ flexWrap: 'inherit' }}>
          <div className="col-11">
            <LabelledOutline
              key={index}
              label={`Call Outcome Rule ${index + 1}`}
            >
              <FormikSelect
                name={`cadenceDispositionRules.${index}.businessDispositionId`}
                className={classes.fieldInsideRow}
                formik={formik}
                label="Call Outcomes"
                size="small"
                variant="outlined"
              >
                {!!formik.values.cadenceDispositionRules[index]
                  .businessDispositionId && (
                  <MenuItem
                    value={
                      Number(
                        formik.values.cadenceDispositionRules[index]
                          ?.businessDispositionId
                      ) ?? ''
                    }
                  >
                    {dispoList?.find(
                      (d) =>
                        Number(
                          formik.values.cadenceDispositionRules[index]
                            .businessDispositionId
                        ) === Number(d.businessDispositionid)
                    )?.disposition ?? ''}
                  </MenuItem>
                )}
                {filteredDispoList?.map((dispo, index) => (
                  <MenuItem key={index} value={dispo.businessDispositionid}>
                    {dispo.disposition}
                  </MenuItem>
                )) ?? []}
              </FormikSelect>
              <FormikSelect
                name={`cadenceDispositionRules.${index}.action`}
                className={classes.fieldInsideRow}
                formik={formik}
                label="Action"
                size="small"
                variant="outlined"
              >
                {filterOptions(index).map((option, index) => (
                  <MenuItem value={option.value} key={index}>
                    {option.text}
                  </MenuItem>
                ))}
              </FormikSelect>
              {formik.values.cadenceDispositionRules[index].action ===
                'forward' && (
                <FormikSelect
                  name={`cadenceDispositionRules.${index}.forwardToCadenceId`}
                  className={classes.fieldInsideRow}
                  formik={formik}
                  label="Cadence"
                  size="small"
                  variant="outlined"
                >
                  {filterCadences(index).map((cadence, index) => (
                    <MenuItem key={index} value={cadence.cadenceId}>
                      {cadence.cadenceName}
                    </MenuItem>
                  ))}
                </FormikSelect>
              )}
              {formik.values.cadenceDispositionRules[index].action ===
                'forwardToPowerlist' && (
                <FormikSelect
                  name={`cadenceDispositionRules.${index}.forwardToPowerlistId`}
                  className={classes.fieldInsideRow}
                  formik={formik}
                  label="PowerList"
                  size="small"
                  variant="outlined"
                >
                  {filterPowerlists(index).map((powerlist, index) => (
                    <MenuItem key={index} value={powerlist.powerlistId}>
                      {powerlist.name}
                    </MenuItem>
                  ))}
                </FormikSelect>
              )}
            </LabelledOutline>
          </div>
          <div
            className="col-1"
            style={{
              marginLeft: 'auto',
              display: 'flex',
              flexWrap: 'nowrap',
            }}
          >
            <Tooltip title="add">
              <>
                <IconButton
                  style={{ marginTop: '8px' }}
                  aria-label="add"
                  color="primary"
                  className={classes.icon}
                  disabled={filteredDispoList?.length === 0}
                  onClick={() => {
                    formik.setFieldValue('cadenceDispositionRules', [
                      ...formik.values.cadenceDispositionRules.slice(
                        0,
                        index + 1
                      ),
                      {
                        businessDispositionId: '',
                        action: '',
                        forwardToCadenceId: '',
                        forwardToPowerlistId: '',
                      },
                    ]);
                  }}
                  size="large"
                >
                  <PlaylistAddIcon />
                </IconButton>
              </>
            </Tooltip>
            <Tooltip key={index} title="remove">
              <>
                <IconButton
                  className={classes.deleteIconButton}
                  style={{ marginTop: '8px' }}
                  aria-label="cancel"
                  onClick={() => {
                    formik.setFieldValue(
                      'cadenceDispositionRules',
                      formik.values.cadenceDispositionRules.length > 1
                        ? [
                            ...formik.values.cadenceDispositionRules.slice(
                              0,
                              index
                            ),
                            ...formik.values.cadenceDispositionRules.slice(
                              index + 1
                            ),
                          ]
                        : [
                            {
                              businessDispositionId: '',
                              action: '',
                              forwardToCadenceId: '',
                              forwardToPowerlistId: '',
                            },
                          ]
                    );
                  }}
                  size="large"
                >
                  <DeleteIcon />
                </IconButton>
              </>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  );
}
